import { useParams, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { setupApiInterceptors } from '../services/api'
import api from '../services/api'

interface InviteDetails {
  petName: string
  species: string
  ownerName: string
  permission: string
  status: string
}

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>()
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0()
  const [invite, setInvite] = useState<InviteDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) return
    fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/shares/invite/${token}`)
      .then(res => res.ok ? res.json() : Promise.reject('not found'))
      .then(data => setInvite(data))
      .catch(() => setError('not_found'))
      .finally(() => setLoading(false))
  }, [token])

  useEffect(() => {
    if (isAuthenticated) {
      setupApiInterceptors(getAccessTokenSilently)
    }
  }, [isAuthenticated, getAccessTokenSilently])

  const acceptInvite = async () => {
    setAccepting(true)
    try {
      await api.post(`/api/shares/accept/${token}`)
      setAccepted(true)
    } catch {
      setError('accept_failed')
    }
    setAccepting(false)
  }

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400"></div>
      </div>
    )
  }

  if (error === 'not_found' || !invite) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">&#128279;</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Invite Not Found</h1>
          <p className="text-gray-600 mb-8">This invite link may have expired or been revoked.</p>
          <Link to="/" className="btn-primary">Go Home</Link>
        </div>
      </div>
    )
  }

  if (accepted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">&#127881;</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Invite Accepted!</h1>
          <p className="text-gray-600 mb-4">{invite.petName} has been added to your shared pets.</p>
          <Link to="/pets" className="btn-primary">View My Pets</Link>
        </div>
      </div>
    )
  }

  if (invite.status !== 'pending') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Invite {invite.status}</h1>
          <p className="text-gray-600 mb-8">This invite has already been {invite.status}.</p>
          <Link to="/" className="btn-primary">Go Home</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center">
      <div className="max-w-md mx-auto px-4">
        <div className="card text-center">
          <div className="text-5xl mb-4">&#128054;</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Pet Share Invite</h1>
          <p className="text-gray-600 mb-6">
            <strong>{invite.ownerName}</strong> wants to share <strong>{invite.petName}</strong> ({invite.species}) with you.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            You'll have <strong>{invite.permission === 'editor' ? 'full edit' : 'view-only'}</strong> access.
          </p>

          {isAuthenticated ? (
            <div className="space-y-3">
              <button
                onClick={acceptInvite}
                disabled={accepting}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {accepting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                {accepting ? 'Accepting...' : 'Accept Invite'}
              </button>
              <Link to="/" className="block text-sm text-gray-500 hover:text-gray-700">
                Decline
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">Sign in to accept this invite</p>
              <button onClick={() => loginWithRedirect()} className="btn-primary w-full">
                Sign In
              </button>
            </div>
          )}

          {error === 'accept_failed' && (
            <p className="text-red-500 text-sm mt-4">Failed to accept. Please try again.</p>
          )}
        </div>
      </div>
    </div>
  )
}
